/* Core & libraries */
import React, { useEffect, useState } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import moment from "helpers/moment";
import Popover from "react-bootstrap/Popover";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";

/* Helpers, constants, custom methods, internal libraries */
import { useCompaniesService } from "../../../services/companiesService";
import { useUserContext } from "../../../contexts/userContext";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useAnalytics } from "../../../hooks/useAnalytics";
import DeleteActionModal from "../../modals/deleteActionModal";
import { useLocaleContext } from "../../../contexts/localeContext.js";

/* Components */
import AddBookingFieldModal from "../../modals/addBookingFieldModal";
import UCButton from "../../buttons";
import DomainsTabSkeleton from "../skeletonDomainsTab";
import { ReactComponent as VerticalDotsSvg } from "../../../resources/images/dots-vertical.svg";
import { ReactComponent as DeleteIcon } from "../../../resources/images/delete-icon.svg";
import { ReactComponent as NoDataIcon } from "../../../resources/images/booking-fields-no-data-icon.svg";

/* Styles */
import styles from "./styles.js";

const BookingFieldsTab = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const [bookingFieldsResponse, setBookingFieldsResponse] = useState({});
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [showDeleteBookingFieldModal, setShowDeleteBookingFieldModal] =
    useState(false);
  const [showAddBookingFieldModal, setShowAddBookingFieldModal] =
    useState(false);
  const [showEditBookingFieldModal, setShowEditBookingFieldModal] =
    useState(false);
  const [overlayOpen, setOverlayOpen] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);

  const globalLocale = useLocaleContext();
  const userDetails = useUserContext();
  const { checkPermission } = useHasPermission();
  const { getBookingFields, addEditBookingField, removeBookingField } =
    useCompaniesService();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;

  useEffect(() => {
    getBookingFieldsList();
  }, []);

  const getBookingFieldsList = async () => {
    setShowSkeleton(true);
    const response = await getBookingFields(companyEntityId);
    if (response) {
      setBookingFieldsResponse(response);
    }
    setShowSkeleton(false);
  };

  const handleEdit = (item) => {
    document.getElementById("root").click();
    setCurrentItem(item);
    setShowEditBookingFieldModal(true);
  };

  const handleDelete = (item) => {
    document.getElementById("root").click();
    setCurrentItem(item);
    setShowDeleteBookingFieldModal(true);
  };

  const handleSubmit = async (values) => {
    const response = await addEditBookingField(companyEntityId, values);
    if (response) {
      getBookingFieldsList();
      setShowAddBookingFieldModal(false);
      setShowEditBookingFieldModal(false);
      setCurrentItem(null);
    }
  };

  const deleteAction = async () => {
    const response = await removeBookingField(
      companyEntityId,
      currentItem?.bookingFieldId
    );
    if (response) {
      getBookingFieldsList();
      setShowDeleteBookingFieldModal(false);
      setCurrentItem(null);
    }
  };

  const renderPopover = (item) => {
    return (
      <Popover className={classes.popoverStyle}>
        <div className={classes.popoverDiv}>
          <div
            className={classes.popoverAction}
            onClick={() => handleEdit(item)}
          >
            <FormattedMessage id={`settings.bookingFields.edit`} />
          </div>
          <div
            className={classes.popoverActionDelete}
            onClick={() => handleDelete(item)}
          >
            <FormattedMessage id={`settings.bookingFields.delete`} />
          </div>
        </div>
      </Popover>
    );
  };

  const renderNoBookingFields = () => {
    return (
      <div className={classes.noData}>
        <NoDataIcon className={classes.iconColor} />
        <p className={classes.noDataTitle}>
          <FormattedMessage id={"settings.bookingFields.noDataTitle"} />
        </p>
        <p className={classes.noDataDescription}>
          <FormattedMessage id={"settings.bookingFields.noDataDescription"} />
        </p>
      </div>
    );
  };

  const handleLeaveClick = (keyValue) => {
    if (keyValue === overlayOpen) {
      setOverlayOpen(null);
    }
  };

  const handleEnterClick = (key) => {
    setOverlayOpen(key);
  };

  return (
    <>
      <div className={classes.details}>
        <div className={classes.txtDiv}>
          <div className={classes.title}>
            <FormattedMessage id="settings.bookingFields.title" />
          </div>
          <div className={classes.description}>
            <FormattedMessage id="settings.bookingFields.description" />
          </div>
        </div>
        <div className={classes.btnContainer}>
          {!showSkeleton &&
            checkPermission("F_COMPANYSETTINGS_MANAGEBOOKINGFIELDS") && (
              <UCButton
                type="primary"
                className={classes.button}
                onClick={() => setShowAddBookingFieldModal(true)}
                text={<FormattedMessage id="settings.bookingFields.add" />}
                disabled={
                  bookingFieldsResponse?.bookingFields?.length >=
                  bookingFieldsResponse?.fieldsLimit
                }
              />
            )}
        </div>
      </div>
      {showSkeleton ? (
        <DomainsTabSkeleton />
      ) : (
        <div className={classes.bookingFieldsTab}>
          <div className={classes.bookingsWrapper}>
            {bookingFieldsResponse?.bookingFields?.length
              ? bookingFieldsResponse?.bookingFields?.map((item, key) => (
                  <div className={classes.row}>
                    <div>
                      <p className={classes.bookingName}>
                        {item.fieldName}
                        {item.required && (
                          <span className={classes.requiredTag}>
                            <FormattedMessage
                              id={"settings.bookingFields.required"}
                            />
                          </span>
                        )}
                      </p>
                      <p className={classes.createdOn}>
                        <FormattedMessage id={"settings.bookingFields.added"} />
                        <span>
                          {moment(item.createdOn)
                            .locale(globalLocale)
                            .format("MMM DD, YYYY")}
                        </span>
                      </p>
                    </div>
                    {checkPermission(
                      "F_COMPANYSETTINGS_MANAGEBOOKINGFIELDS"
                    ) && (
                      <div className={classes.dotsButtonContainer}>
                        <OverlayTrigger
                          trigger="click"
                          rootClose
                          placement="bottom"
                          overlay={renderPopover(item)}
                          onEnter={() => handleEnterClick(key)}
                          onExit={() => handleLeaveClick(key)}
                        >
                          <div
                            className={`${classes.dotsStyle} ${
                              overlayOpen === key ? classes.dotsOpenStyle : ""
                            }`}
                          >
                            <VerticalDotsSvg />
                          </div>
                        </OverlayTrigger>
                      </div>
                    )}
                  </div>
                ))
              : renderNoBookingFields()}
          </div>
          <DeleteActionModal
            showModal={showDeleteBookingFieldModal}
            setShowModal={setShowDeleteBookingFieldModal}
            modalIcon={<DeleteIcon />}
            modalTitle={
              <FormattedMessage id="settings.bookingFields.deleteAction.title" />
            }
            modalDescription={
              <FormattedMessage id="settings.bookingFields.deleteAction.description" />
            }
            submitText={
              <FormattedMessage id="settings.bookingFields.deleteAction.remove" />
            }
            submitCallback={deleteAction}
            setSelectedRow={(value) => {}}
          />
          <AddBookingFieldModal
            showModal={showAddBookingFieldModal}
            setShowModal={setShowAddBookingFieldModal}
            editItem={null}
            handleSubmit={handleSubmit}
          />
          <AddBookingFieldModal
            showModal={showEditBookingFieldModal}
            setShowModal={setShowEditBookingFieldModal}
            editItem={currentItem}
            handleSubmit={handleSubmit}
          />
        </div>
      )}
    </>
  );
};

export default BookingFieldsTab;
